// src/pages/Login.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

const LS_POST_LOGIN = "pwp_post_login_path";
const LS_PKCE_RETRY = "pwp_pkce_retry_once";

function sanitizeBackPath(back) {
  if (typeof back !== "string") return "/pwp-payment-success?next=/level-2";
  if (!back.startsWith("/")) return "/pwp-payment-success?next=/level-2";
  if (back.startsWith("//")) return "/pwp-payment-success?next=/level-2";
  return back;
}

export default function Login() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");

  // "email" | "otp"
  const [step, setStep] = useState("email");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);

  const navigate = useNavigate();
  const { search } = useLocation();
  const q = useMemo(() => new URLSearchParams(search), [search]);

  const back = sanitizeBackPath(
    q.get("back") || "/pwp-payment-success?next=/level-2"
  );

  useEffect(() => {
    sessionStorage.setItem(LS_POST_LOGIN, back);
  }, [back]);

  // If already logged in, skip login screen
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!alive) return;
        if (data?.session) navigate(back, { replace: true });
      } catch {
        // ignore
      }
    })();
    return () => { alive = false; };
  }, [navigate, back]);

  // Auto-focus first OTP box when step changes
  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
    }
  }, [step]);

  const redirectTo = `${window.location.origin}/auth/callback`;

  // ── Google ──────────────────────────────────────────────
  const signInWithGoogle = async () => {
    setErr("");
    setBusy(true);
    try {
      sessionStorage.removeItem(LS_PKCE_RETRY);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      });
      if (error) setErr(error.message);
    } catch (e) {
      setErr(e?.message || "Could not start Google sign-in.");
    } finally {
      setBusy(false);
    }
  };

  // ── Step 1: send OTP ────────────────────────────────────
  const sendOtp = async () => {
    setErr("");
    const clean = String(email || "").trim().toLowerCase();
    if (!clean.includes("@")) {
      setErr("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      sessionStorage.removeItem(LS_PKCE_RETRY);
      const { error } = await supabase.auth.signInWithOtp({
        email: clean,
        options: {
          // OTP only — no magic link — keeps user on page
          shouldCreateUser: true,
          emailRedirectTo: redirectTo,
        },
      });
      if (error) {
        setErr(error.message);
      } else {
        setOtp(["", "", "", "", "", ""]);
        setStep("otp");
      }
    } catch (e) {
      setErr(e?.message || "Could not send code.");
    } finally {
      setBusy(false);
    }
  };

  // ── Step 2: verify OTP ──────────────────────────────────
  const verifyOtp = async () => {
    setErr("");
    const code = otp.join("");
    if (code.length < 6) {
      setErr("Please enter the full 6-digit code.");
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: String(email).trim().toLowerCase(),
        token: code,
        type: "email",
      });
      if (error) {
        setErr(error.message);
      } else if (data?.session) {
        navigate(back, { replace: true });
      } else {
        setErr("Verification failed — please try again.");
      }
    } catch (e) {
      setErr(e?.message || "Could not verify code.");
    } finally {
      setBusy(false);
    }
  };

  // ── OTP box handlers ────────────────────────────────────
  const handleOtpChange = (index, value) => {
    // Accept paste of full 6-digit code into any box
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6).split("");
      const next = ["", "", "", "", "", ""];
      digits.forEach((d, i) => { next[i] = d; });
      setOtp(next);
      const focusIdx = Math.min(digits.length, 5);
      otpRefs.current[focusIdx]?.focus();
      return;
    }
    const digit = value.replace(/\D/g, "");
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") verifyOtp();
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + "*".repeat(Math.min(b.length, 4)) + c)
    : "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-purple-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border shadow-lg p-6">

        {/* Header */}
        <div className="text-center">
          <div className="text-3xl mb-2">🎹</div>
          <h1 className="text-2xl font-extrabold text-slate-900">
            {step === "email" ? "Welcome to Piano With Pan" : "Check your email"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {step === "email"
              ? "Sign in to unlock your full piano journey"
              : `We sent a 6-digit code to ${maskedEmail}`}
          </p>
        </div>

        {/* Error */}
        {err && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {/* ── Step 1: Email + Google ── */}
        {step === "email" && (
          <>
            <button
              onClick={signInWithGoogle}
              disabled={busy}
              className="mt-5 w-full rounded-xl bg-purple-600 text-white font-extrabold py-3 hover:bg-purple-700 transition disabled:opacity-60"
            >
              {busy ? "Opening Google…" : "Continue with Google"}
            </button>

            <div className="mt-5 flex items-center gap-3">
              <div className="flex-1 border-t border-slate-200" />
              <span className="text-xs font-bold text-slate-400">OR</span>
              <div className="flex-1 border-t border-slate-200" />
            </div>

            <div className="mt-4">
              <label className="text-xs font-bold text-slate-700">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                placeholder="you@gmail.com"
                className="mt-1 w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                autoComplete="email"
                type="email"
              />
              <button
                onClick={sendOtp}
                disabled={busy}
                className="mt-3 w-full rounded-xl border bg-white font-extrabold py-3 hover:bg-gray-50 transition disabled:opacity-60 text-slate-800"
              >
                {busy ? "Sending code…" : "Continue with Email →"}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: OTP entry ── */}
        {step === "otp" && (
          <>
            {/* 6-box OTP input */}
            <div className="mt-6 flex justify-center gap-2">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className="w-11 h-14 text-center text-xl font-extrabold rounded-xl border-2 border-slate-200 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100 transition bg-white text-slate-900"
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            <button
              onClick={verifyOtp}
              disabled={busy || otp.join("").length < 6}
              className="mt-5 w-full rounded-xl bg-purple-600 text-white font-extrabold py-3 hover:bg-purple-700 transition disabled:opacity-50"
            >
              {busy ? "Verifying…" : "Confirm & Sign In"}
            </button>

            {/* Resend */}
            <div className="mt-4 text-center">
              <span className="text-xs text-slate-500">Didn't get a code? </span>
              <button
                onClick={() => { setStep("email"); setErr(""); setOtp(["","","","","",""]); }}
                className="text-xs font-bold text-purple-600 hover:text-purple-800 underline underline-offset-2"
              >
                Try again
              </button>
            </div>
          </>
        )}

        {/* Back */}
        <button
          onClick={() => step === "otp" ? (setStep("email"), setErr("")) : navigate(back)}
          className="mt-4 w-full rounded-xl border py-3 font-bold bg-white hover:bg-gray-50 transition text-slate-700 text-sm"
        >
          {step === "otp" ? "← Change email" : "Back"}
        </button>

        <div className="mt-3 text-[11px] text-slate-400 text-center">
          We don't post anything. You can sign out anytime.
        </div>
      </div>
    </div>
  );
}